import React from "react";
import { Switch } from "@/components/ui/switch";
import { Plus, X, Copy } from "lucide-react";

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

function normalize(struct) {
  const base = DAYS.map(() => ({ closed: true, ranges: [{ from: "09:00", to: "19:00" }] }));
  if (Array.isArray(struct)) {
    struct.forEach((d, i) => {
      if (i < 7 && d) {
        const ranges = Array.isArray(d.ranges) && d.ranges.length
          ? d.ranges.filter((r) => r).map((r) => ({ from: r.from || "09:00", to: r.to || "19:00" }))
          : [{ from: "09:00", to: "19:00" }];
        base[i] = { closed: !!d.closed, ranges: ranges.length ? ranges : [{ from: "09:00", to: "19:00" }] };
      }
    });
  }
  return base;
}

export default function HoursEditor({ value, onChange }) {
  const days = normalize(value);
  const update = (i, patch) => onChange(days.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));

  const setRange = (i, ri, key, v) => {
    const ranges = days[i].ranges.map((r, idx) => (idx === ri ? { ...r, [key]: v } : r));
    update(i, { ranges });
  };
  const addRange = (i) => {
    const last = days[i].ranges[days[i].ranges.length - 1];
    const next = last && last.to >= "18:00" ? { from: "20:00", to: "23:00" } : { from: "18:30", to: "22:30" };
    update(i, { ranges: [...days[i].ranges, next] });
  };
  const removeRange = (i, ri) => {
    const ranges = days[i].ranges.filter((_, idx) => idx !== ri);
    update(i, { ranges: ranges.length ? ranges : [{ from: "09:00", to: "19:00" }] });
  };
  const copyToAll = (i) => {
    const src = days[i];
    const clone = { closed: src.closed, ranges: src.ranges.map((r) => ({ ...r })) };
    onChange(days.map(() => ({ closed: clone.closed, ranges: clone.ranges.map((r) => ({ ...r })) })));
  };

  return (
    <div className="space-y-2.5" data-testid="hours-editor">
      {days.map((d, i) => (
        <div key={i} className="py-2 border-b border-slate-100 last:border-0">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">{DAYS[i]}</span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-400">{d.closed ? "Chiuso" : "Aperto"}</span>
              <Switch checked={!d.closed} onCheckedChange={(v) => update(i, { closed: !v })} data-testid={`hours-open-${i}`} />
            </div>
          </div>

          {!d.closed && (
            <div className="mt-2 space-y-2">
              {d.ranges.map((r, ri) => (
                <div key={ri} className="flex items-center gap-2">
                  <input type="time" value={r.from} onChange={(e) => setRange(i, ri, "from", e.target.value)}
                    data-testid={`hours-from-${i}-${ri}`} className="h-9 px-2 rounded-lg border border-slate-200 text-sm flex-1 min-w-0" />
                  <span className="text-slate-400">–</span>
                  <input type="time" value={r.to} onChange={(e) => setRange(i, ri, "to", e.target.value)}
                    data-testid={`hours-to-${i}-${ri}`} className="h-9 px-2 rounded-lg border border-slate-200 text-sm flex-1 min-w-0" />
                  {d.ranges.length > 1 && (
                    <button type="button" onClick={() => removeRange(i, ri)} data-testid={`hours-remove-${i}-${ri}`}
                      className="h-9 w-9 rounded-lg border border-slate-200 text-slate-400 flex items-center justify-center shrink-0 hover:bg-slate-50">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => addRange(i)} data-testid={`hours-add-${i}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E63946] hover:underline">
                <Plus className="h-3.5 w-3.5" /> Aggiungi fascia (es. cena)
              </button>
            </div>
          )}
          <button type="button" onClick={() => copyToAll(i)} data-testid={`hours-copy-${i}`}
            className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 hover:underline">
            <Copy className="h-3.5 w-3.5" /> Copia su tutti i giorni
          </button>
        </div>
      ))}
    </div>
  );
}
